// Liveries: what a maker customizes (colors, pattern, number) and the canvas
// painter that turns it into a texture for a lofted body (see loft.ts for the
// UV layout: u along the length, v around the section).
//
// Canvas layout (x = u, rear → front; y = (1 - v) × height):
//   y = 0..H/2   left side, bottom (y=0) to top (y=H/2)
//   y = H/2      top centre line
//   y = H/2..H   right side, top (y=H/2) to bottom (y=H)
export type Pattern = 'solid' | 'stripe' | 'twin' | 'split' | 'arrow' | 'wave'
export const PATTERNS: Pattern[] = ['stripe', 'twin', 'split', 'arrow', 'wave', 'solid']

export type Livery = {
  primary: string
  secondary: string
  accent: string
  pattern: Pattern
  number: number | string
  name: string
  // Endurance: the class color goes on the number panel, as in real multiclass racing.
  panel?: string
  // The product's logo (a PNG URL) for the name stickers.
  logo?: string | null
  // A wide (16:9) logo is a wordmark: it replaces the name on the stickers.
  logo_wide?: boolean
}

export type Placement = {
  numberU: number // where the number sits along the body (0 rear, 1 front); < 0: none
  numberHeight: number // 0 bottom .. 1 top of the side
  numberSize: number // fraction of the canvas height
  nameU: number // < 0: none
  nameHeight: number
  nameSize: number
  aspect: number // how much the v axis is squashed on this body (text correction)
}

export const W = 1024
export const H = 512

// Side point: side = 'left' | 'right', u 0..1 along, height 0 (bottom) .. 1 (top).
export function sideXY(side: 'left' | 'right', u: number, height: number): [number, number] {
  const x = u * W
  const y = side === 'left' ? height * (H / 2) : H - height * (H / 2)
  return [x, y]
}

export function contrast(hex: string): string {
  const n = parseInt(hex.replace('#', ''), 16)
  const r = (n >> 16) & 255
  const g = (n >> 8) & 255
  const b = n & 255
  return 0.299 * r + 0.587 * g + 0.114 * b > 150 ? '#0E0E0E' : '#F7F7F5'
}

type Ctx = CanvasRenderingContext2D

function polygonBothSides(x: Ctx, pts: [number, number][], color: string) {
  x.fillStyle = color
  for (const side of ['left', 'right'] as const) {
    x.beginPath()
    pts.forEach(([u, h], i) => {
      const [px, py] = sideXY(side, u, h)
      if (i === 0) x.moveTo(px, py)
      else x.lineTo(px, py)
    })
    x.closePath()
    x.fill()
  }
}

function paintPattern(x: Ctx, l: Livery) {
  x.fillStyle = l.primary
  x.fillRect(0, 0, W, H)
  switch (l.pattern) {
    case 'stripe':
      x.fillStyle = l.secondary
      x.fillRect(0, H / 2 - 40, W, 80)
      x.fillStyle = l.accent
      x.fillRect(0, H / 2 - 52, W, 8)
      x.fillRect(0, H / 2 + 44, W, 8)
      break
    case 'twin':
      x.fillStyle = l.secondary
      x.fillRect(0, H / 2 - 62, W, 34)
      x.fillRect(0, H / 2 + 28, W, 34)
      break
    case 'split':
      polygonBothSides(x, [[0, 0], [1, 0], [1, 0.38], [0.55, 0.5], [0, 0.62]], l.secondary)
      polygonBothSides(x, [[0, 0.62], [0.55, 0.5], [1, 0.38], [1, 0.43], [0.55, 0.55], [0, 0.67]], l.accent)
      x.fillStyle = l.secondary
      x.fillRect(0, 0, W, 18)
      x.fillRect(0, H - 18, W, 18)
      break
    case 'arrow':
      polygonBothSides(x, [[1, 0.1], [0.62, 0.1], [0.18, 0.9], [0.38, 0.9]], l.secondary)
      polygonBothSides(x, [[0.66, 0.1], [0.6, 0.1], [0.16, 0.9], [0.22, 0.9]], l.accent)
      x.fillStyle = l.secondary
      x.fillRect(0.72 * W, H / 2 - 26, 0.28 * W, 52)
      break
    case 'wave':
      x.fillStyle = l.secondary
      for (const side of ['left', 'right'] as const) {
        x.beginPath()
        for (let i = 0; i <= 40; i++) {
          const u = i / 40
          const [px, py] = sideXY(side, u, 0.42 + 0.18 * Math.sin(u * Math.PI * 2.2))
          if (i === 0) x.moveTo(px, py)
          else x.lineTo(px, py)
        }
        const [ex, ey] = sideXY(side, 1, 0)
        const [sx, sy] = sideXY(side, 0, 0)
        x.lineTo(ex, ey)
        x.lineTo(sx, sy)
        x.closePath()
        x.fill()
      }
      break
    case 'solid':
      break
  }
}

// Text on a side, readable from that side (right side mirrored horizontally,
// left side mirrored vertically, because of how the loft wraps).
function sideText(x: Ctx, side: 'left' | 'right', u: number, height: number, draw: () => void, aspect: number) {
  const [px, py] = sideXY(side, u, height)
  x.save()
  x.translate(px, py)
  if (side === 'right') x.scale(-1, 1 / aspect)
  else x.scale(1, -1 / aspect)
  draw()
  x.restore()
}

export function paintLivery(canvas: HTMLCanvasElement, l: Livery, p: Placement): void {
  canvas.width = W
  canvas.height = H
  const x = canvas.getContext('2d')
  if (!x) return
  paintPattern(x, l)

  const size = p.numberSize * H
  const panel = l.panel ?? '#F7F7F5'
  for (const side of ['left', 'right'] as const) {
    // Number panel.
    if (p.numberU >= 0) sideText(x, side, p.numberU, p.numberHeight, () => {
      x.fillStyle = panel
      x.beginPath()
      x.roundRect(-size * 0.62, -size * 0.55, size * 1.24, size * 1.1, size * 0.18)
      x.fill()
      x.fillStyle = contrast(panel)
      x.textAlign = 'center'
      x.textBaseline = 'middle'
      x.font = `italic 800 ${Math.round(size * 0.95)}px "Saira Condensed", "Arial Narrow", sans-serif`
      x.fillText(String(l.number), 0, size * 0.05, size * 1.15)
    }, p.aspect)
    // The product's name, like a title sponsor.
    if (p.nameU >= 0) sideText(x, side, p.nameU, p.nameHeight, () => {
      x.fillStyle = contrast(l.pattern === 'split' && p.nameHeight < 0.5 ? l.secondary : l.primary)
      x.textAlign = 'center'
      x.textBaseline = 'middle'
      x.font = `italic 800 ${Math.round(p.nameSize * H)}px "Saira Condensed", "Arial Narrow", sans-serif`
      x.fillText(l.name.toUpperCase(), 0, 0, W * 0.32)
    }, p.aspect)
  }
}

// Trim panels (wings, fenders, mirrors): the pattern only.
export function paintTrim(canvas: HTMLCanvasElement, l: Livery): void {
  canvas.width = 256
  canvas.height = 128
  const x = canvas.getContext('2d')
  if (!x) return
  x.save()
  x.scale(256 / W, 128 / H)
  paintPattern(x, l)
  x.restore()
}

// ── Planar liveries for sculpted bodies (sculpt.ts) ─────────────────────
// Canvas 1024 × 1024: the top half is the side view (front on the right),
// the bottom half is the plan view (left side at the bottom edge). Everything
// is drawn in metres.
export type PlanarBox = { zMin: number; zMax: number; yMin: number; yMax: number; xMax: number }
export type PlanarLayout = { numberZ: number; numberY: number; numberH: number; nameZ: number; nameY: number; nameH: number; beltY: number }

export const PLANAR = 1024

export function paintPlanar(canvas: HTMLCanvasElement, l: Livery, box: PlanarBox, layout: PlanarLayout): void {
  canvas.width = PLANAR
  canvas.height = PLANAR
  const x = canvas.getContext('2d')
  if (!x) return
  const L = box.zMax - box.zMin
  const H = box.yMax - box.yMin
  const Wd = box.xMax * 2
  x.fillStyle = l.primary
  x.fillRect(0, 0, PLANAR, PLANAR)

  // Side view in metres: (z, y) → pixels.
  const side = (draw: () => void) => {
    x.save()
    x.setTransform(PLANAR / L, 0, 0, -(PLANAR / 2) / H, -box.zMin * (PLANAR / L), (PLANAR / 2) * (1 + box.yMin / H))
    draw()
    x.restore()
  }
  // Plan view in metres: (z, x) → pixels (x = 0 is the centre line).
  const plan = (draw: () => void) => {
    x.save()
    x.setTransform(PLANAR / L, 0, 0, -(PLANAR / 2) / Wd, -box.zMin * (PLANAR / L), PLANAR * 0.75)
    draw()
    x.restore()
  }
  const poly = (pts: [number, number][], color: string) => {
    x.fillStyle = color
    x.beginPath()
    pts.forEach(([a, b], i) => (i ? x.lineTo(a, b) : x.moveTo(a, b)))
    x.closePath()
    x.fill()
  }
  const z0 = box.zMin
  const z1 = box.zMax
  const belt = layout.beltY

  switch (l.pattern) {
    case 'stripe':
      plan(() => {
        poly([[z0, -0.2], [z1, -0.2], [z1, 0.2], [z0, 0.2]], l.secondary)
        poly([[z0, -0.27], [z1, -0.27], [z1, -0.23], [z0, -0.23]], l.accent)
        poly([[z0, 0.23], [z1, 0.23], [z1, 0.27], [z0, 0.27]], l.accent)
      })
      break
    case 'twin':
      plan(() => {
        poly([[z0, -0.34], [z1, -0.34], [z1, -0.2], [z0, -0.2]], l.secondary)
        poly([[z0, 0.2], [z1, 0.2], [z1, 0.34], [z0, 0.34]], l.secondary)
      })
      break
    case 'split':
      side(() => {
        poly([[z0, 0], [z1, 0], [z1, belt * 0.62], [z0, belt * 0.86]], l.secondary)
        poly([[z0, belt * 0.86], [z1, belt * 0.62], [z1, belt * 0.68], [z0, belt * 0.92]], l.accent)
      })
      break
    case 'arrow':
      side(() => {
        poly([[z1, belt * 0.25], [z1 - L * 0.12, belt * 0.25], [z0 + L * 0.2, belt * 1.1], [z0 + L * 0.34, belt * 1.1]], l.secondary)
        poly([[z1 - L * 0.14, belt * 0.25], [z1 - L * 0.18, belt * 0.25], [z0 + L * 0.16, belt * 1.1], [z0 + L * 0.2, belt * 1.1]], l.accent)
      })
      plan(() => poly([[z1 - L * 0.3, -0.16], [z1, -0.16], [z1, 0.16], [z1 - L * 0.3, 0.16]], l.secondary))
      break
    case 'wave':
      side(() => {
        x.fillStyle = l.secondary
        x.beginPath()
        x.moveTo(z0, 0)
        for (let i = 0; i <= 48; i++) {
          const t = i / 48
          x.lineTo(z0 + t * L, belt * (0.62 + 0.22 * Math.sin(t * Math.PI * 2.1)))
        }
        x.lineTo(z1, 0)
        x.closePath()
        x.fill()
      })
      break
    case 'solid':
      break
  }

  // Number panel (class color in Endurance) and the product name.
  side(() => {
    const h = layout.numberH
    x.fillStyle = l.panel ?? '#F7F7F5'
    x.beginPath()
    x.roundRect(layout.numberZ - h * 0.62, layout.numberY - h * 0.5, h * 1.24, h, h * 0.16)
    x.fill()
    x.save()
    x.translate(layout.numberZ, layout.numberY)
    x.scale(h / 100, -h / 100) // draw at 100 px = h metres
    x.fillStyle = contrast(l.panel ?? '#F7F7F5')
    x.textAlign = 'center'
    x.textBaseline = 'middle'
    x.font = 'italic 800 90px "Saira Condensed", "Arial Narrow", sans-serif'
    x.fillText(String(l.number), 0, 5, 115)
    x.restore()

    x.save()
    x.translate(layout.nameZ, layout.nameY)
    x.scale(layout.nameH / 100, -layout.nameH / 100)
    const under = l.pattern === 'split' && layout.nameY < belt * 0.75 ? l.secondary : l.primary
    x.fillStyle = contrast(under)
    x.textAlign = 'center'
    x.textBaseline = 'middle'
    x.font = 'italic 800 100px "Saira Condensed", "Arial Narrow", sans-serif'
    x.fillText(l.name.toUpperCase(), 0, 0, (L * 0.3 * 100) / layout.nameH)
    x.restore()
  })
}
